import * as React from "react";
import {
  TrendingUp,
  Trophy,
  PieChart,
  AlertTriangle,
  DollarSign,
  Globe,
  BarChart2,
  Lightbulb,
  FileText,
  X,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

import { cn, formatRelativeTime } from "../../lib/utils";
import { AppLayout } from "../../components/ui-v2/layout/AppLayout";
import { Card, CardContent } from "../../components/ui-v2/card";
import { Button } from "../../components/ui-v2/button";
import { Skeleton } from "../../components/ui-v2/skeleton";
import { EmptyState } from "../../components/ui-v2/empty-state";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/ui-v2/tooltip";
import { Text } from "../../components/ui-v2/typography";
import { useInsights } from "../../hooks/useInsights";

/**
 * Insights Page — Institutional Console redesign (issue #237,
 * DESIGN-SPEC.md §5 "Insights", §4.6 "Insight card / event rows").
 *
 * Layout: sticky left category rail with per-category counts + card feed
 * (max-width 1240, DESIGN-SPEC §2.4 "feed/list screens") + Load more.
 * "Mark all read" / "Refresh" moved into the toolbar's secondary-actions
 * slot (AppLayout), replacing the old header-content CountBadge + inline
 * Card-based filter row. All existing functionality retained: category
 * filter, mark-all-read, per-insight mark-as-read (on click), dismiss,
 * cursor-based pagination (Load more), auto-refresh (via useInsights),
 * loading/error/empty states.
 */

/**
 * Category configuration - icons and labels (colors now driven by §4.6's
 * category-badge tint table via CATEGORY_BADGE_STYLE below; iconography
 * kept for the rail).
 */
const CATEGORY_CONFIG = {
  movement: { icon: TrendingUp, label: "Movement" },
  milestone: { icon: Trophy, label: "Milestone" },
  allocation: { icon: PieChart, label: "Allocation" },
  risk: { icon: AlertTriangle, label: "Risk" },
  dividend: { icon: DollarSign, label: "Dividend" },
  market: { icon: Globe, label: "Market" },
  performance: { icon: BarChart2, label: "Performance" },
  recommendation: { icon: Lightbulb, label: "Tip" },
  summary: { icon: FileText, label: "Summary" },
};

/**
 * All category keys for the left rail (§5 "left category rail w/ counts").
 */
const ALL_CATEGORIES = [
  "movement",
  "milestone",
  "allocation",
  "risk",
  "dividend",
  "market",
  "performance",
  "recommendation",
  "summary",
];

/**
 * Category badge tint (§4.6 insight card header badge). Mirrors
 * Insights.dc.html's `CFG` map; categories not explicitly styled there
 * (milestone/risk/recommendation/summary) fall back to the neutral/warning
 * tints closest to their semantic meaning so every category still renders
 * a distinct, spec-consistent chip rather than an unstyled default.
 *
 * `allocation` uses the `allocation-tint` Tailwind color (bound to
 * `--allocation-tint-text`, console-dark.css/console-light.css) instead of a
 * hardcoded `text-[#b4a8ef]` — round-2 fix, ui-design-expert review finding
 * F1: the hardcoded lavender computed 1.86:1 on the light-theme tint
 * background (fails AA even at the 3:1 large-text carve-out); the new token
 * flips to an AA-passing `#665aa8` in console-light while keeping the
 * mockup's exact `#b4a8ef` in console-dark.
 */
const CATEGORY_BADGE_CLASS = {
  movement: "bg-accent-tint text-accent-text",
  milestone: "bg-[rgba(217,161,59,0.12)] text-warning-tint",
  allocation: "bg-[rgba(139,125,216,0.14)] text-allocation-tint",
  risk: "bg-[rgba(217,161,59,0.12)] text-warning-tint",
  dividend: "bg-[var(--color-state-success-bg)] text-positive-tint",
  market: "bg-[rgba(255,255,255,0.06)] text-text-secondary",
  performance: "bg-[var(--color-state-success-bg)] text-positive-tint",
  recommendation: "bg-accent-tint text-accent-text",
  summary: "bg-[rgba(255,255,255,0.06)] text-text-secondary",
};

/**
 * Get category config with fallback
 */
function getCategoryConfig(category) {
  return CATEGORY_CONFIG[category] || CATEGORY_CONFIG.summary;
}

function getCategoryBadgeClass(category) {
  return CATEGORY_BADGE_CLASS[category] || CATEGORY_BADGE_CLASS.summary;
}

/**
 * Format full timestamp for tooltip display
 */
const formatFullTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Loading skeleton for insights list (rail + feed)
 */
function LoadingState() {
  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-[200px_1fr]">
      <div className="flex flex-col gap-1.5">
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={`rail-${i}`} className="h-8 w-full rounded-md" />
        ))}
      </div>
      <div className="flex flex-col gap-2.5">
        {[1, 2, 3, 4].map((i) => (
          <Card key={`skeleton-${i}`}>
            <CardContent className="py-4">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-16 rounded" />
                <Skeleton className="h-4 w-10 rounded" />
              </div>
              <Skeleton className="mt-2 h-5 w-3/4" />
              <Skeleton className="mt-1.5 h-4 w-full" />
              <Skeleton className="mt-1 h-4 w-2/3" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({ error, onRetry }) {
  return (
    <Card>
      <CardContent className="py-16">
        <div className="flex flex-col items-center justify-center">
          <div className="rounded-full bg-destructive/10 p-4">
            <AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-foreground">Failed to load insights</h2>
          <Text variant="secondary" className="mt-2 max-w-md text-center">
            {error}
          </Text>
          <Button variant="outline" className="mt-6" onClick={onRetry}>
            <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
            Try Again
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * CategoryRail — sticky left category filter list with counts (§5 "left
 * category rail w/ counts"). Counts reflect the currently loaded page's
 * category distribution plus the active filter's total from the API where
 * available; falls back to per-category counts computed from the loaded
 * insights list (best-effort — the API does not return global per-category
 * totals, only the overall unreadCount).
 */
function CategoryRail({ activeCategory, onCategoryChange, categoryCounts, totalCount }) {
  return (
    <nav className="sticky top-5 flex flex-col gap-0.5" aria-label="Insight categories">
      <button
        type="button"
        onClick={() => onCategoryChange(null)}
        aria-pressed={!activeCategory}
        className={cn(
          "flex items-center justify-between rounded-md px-3 py-[7px] text-[12.5px] transition-colors duration-[120ms] ease-out",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]",
          !activeCategory
            ? "bg-accent-tint font-semibold text-accent-text"
            : "text-muted-token hover:bg-[rgba(255,255,255,0.04)]"
        )}
      >
        <span>All</span>
        <span className="font-mono text-[10.5px] text-text-faint">{totalCount}</span>
      </button>
      {ALL_CATEGORIES.map((cat) => {
        const config = getCategoryConfig(cat);
        const isActive = activeCategory === cat;
        const count = categoryCounts[cat] || 0;
        return (
          <button
            key={cat}
            type="button"
            onClick={() => onCategoryChange(isActive ? null : cat)}
            aria-pressed={isActive}
            className={cn(
              "flex items-center justify-between rounded-md px-3 py-[7px] text-[12.5px] transition-colors duration-[120ms] ease-out",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]",
              isActive
                ? "bg-accent-tint font-semibold text-accent-text"
                : "text-muted-token hover:bg-[rgba(255,255,255,0.04)]"
            )}
          >
            <span>{config.label}</span>
            <span className="font-mono text-[10.5px] text-text-faint">{count}</span>
          </button>
        );
      })}
    </nav>
  );
}

/**
 * Single insight card component (§4.6 "Insight card").
 */
function InsightCard({ insight, onMarkAsRead, onDismiss }) {
  const config = getCategoryConfig(insight.category);
  const isUnread = !insight.read;
  const isHighPriority = insight.priority === "high";

  // Mark as read when card is viewed (on click)
  const handleClick = React.useCallback(() => {
    if (isUnread) {
      onMarkAsRead(insight.insightId, insight.date);
    }
  }, [isUnread, insight.insightId, insight.date, onMarkAsRead]);

  // Dismiss handler
  const handleDismiss = React.useCallback((e) => {
    e.stopPropagation();
    onDismiss(insight.insightId, insight.date);
  }, [insight.insightId, insight.date, onDismiss]);

  // Extract tickers from metadata
  const tickers = insight.metadata?.tickers || [];

  return (
    <Card
      className={cn(
        "cursor-pointer transition-colors duration-[120ms] ease-out hover:border-[#2a4763]",
        isUnread && "border-l-2 border-l-[var(--accent,_#3b9eff)]"
      )}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <CardContent className="px-4 py-3.5">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "inline-flex items-center rounded px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em]",
              getCategoryBadgeClass(insight.category)
            )}
          >
            {config.label}
          </span>
          {isHighPriority && (
            <span className="inline-flex items-center rounded bg-[var(--color-state-error-bg)] px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-negative-tint">
              High priority
            </span>
          )}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="ml-auto font-mono text-[11px] text-text-faint">
                  {formatRelativeTime(insight.generatedAt || insight.date)}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p>{formatFullTimestamp(insight.generatedAt || insight.date)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {isUnread && (
            <span
              className="h-[7px] w-[7px] shrink-0 rounded-full bg-[var(--accent,_#3b9eff)]"
              title="Unread"
              aria-label="Unread"
            />
          )}
          <button
            type="button"
            onClick={handleDismiss}
            title="Dismiss insight"
            aria-label="Dismiss insight"
            className="inline-flex h-6 w-6 items-center justify-center rounded text-text-faint transition-colors duration-[120ms] ease-out hover:bg-[rgba(255,255,255,0.06)] hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent,_#3b9eff)]"
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>

        <h3
          className={cn(
            "mt-2 text-[13.5px] leading-snug",
            isUnread ? "font-semibold text-foreground" : "font-medium text-text-secondary"
          )}
        >
          {insight.title}
        </h3>

        <Text size="sm" className="mt-[3px] leading-[1.55] text-muted-token">
          {insight.body}
        </Text>

        {tickers.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {tickers.map((ticker) => (
              <span
                key={ticker}
                className="rounded bg-[rgba(255,255,255,0.05)] px-2 py-0.5 font-mono text-[10.5px] text-muted-token"
              >
                {ticker}
              </span>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Insights Page Component
 */
export function Insights() {
  const {
    insights,
    loading,
    error,
    hasMore,
    unreadCount,
    filters,
    setFilters,
    loadMore,
    refresh,
    markAsRead,
    markAllAsRead,
    dismissInsight,
  } = useInsights();

  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refresh();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLoadMore = async () => {
    setIsLoadingMore(true);
    try {
      await loadMore();
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (err) {
      console.error("Failed to mark all insights as read:", err);
    }
  };

  const handleCategoryChange = (category) => {
    setFilters({ category });
  };

  // Best-effort per-category counts from the currently loaded page (see
  // CategoryRail JSDoc — the API does not expose global per-category totals).
  const categoryCounts = React.useMemo(() => {
    return insights.reduce((acc, insight) => {
      acc[insight.category] = (acc[insight.category] || 0) + 1;
      return acc;
    }, {});
  }, [insights]);

  const toolbarActions = (
    <>
      {unreadCount > 0 && (
        <span className="font-mono text-[11px] text-accent-text">{unreadCount} unread</span>
      )}
      {unreadCount > 0 && (
        <Button variant="outline" size="sm" onClick={handleMarkAllAsRead}>
          Mark all read
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isRefreshing}>
        <RefreshCw
          className={cn("h-3.5 w-3.5", isRefreshing && "animate-spin")}
          aria-hidden="true"
        />
        Refresh
      </Button>
    </>
  );

  // Loading state
  if (loading && insights.length === 0) {
    return (
      <AppLayout title="Daily insights" secondaryActions={toolbarActions}>
        <div className="mx-auto max-w-[1240px]">
          <LoadingState />
        </div>
      </AppLayout>
    );
  }

  // Error state
  if (error && insights.length === 0) {
    return (
      <AppLayout title="Daily insights" secondaryActions={toolbarActions}>
        <div className="mx-auto max-w-[1240px]">
          <ErrorState error={error} onRetry={handleRefresh} />
        </div>
      </AppLayout>
    );
  }

  // Empty state
  if (insights.length === 0) {
    return (
      <AppLayout title="Daily insights" secondaryActions={toolbarActions}>
        <div className="mx-auto max-w-[1240px]">
          <EmptyState
            icon={Lightbulb}
            title="No insights yet"
            description="AI-generated insights about your portfolio will appear here. Check back soon for personalized analysis of your investments."
            primaryAction={{
              label: "Go to Dashboard",
              href: "/",
            }}
          />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Daily insights" secondaryActions={toolbarActions}>
      <div className="mx-auto grid max-w-[1240px] grid-cols-1 items-start gap-5 md:grid-cols-[200px_1fr]">
        <CategoryRail
          activeCategory={filters.category}
          onCategoryChange={handleCategoryChange}
          categoryCounts={categoryCounts}
          totalCount={insights.length}
        />

        <div className="flex flex-col gap-2.5">
          {insights.map((insight) => (
            <InsightCard
              key={insight.insightId}
              insight={insight}
              onMarkAsRead={markAsRead}
              onDismiss={dismissInsight}
            />
          ))}

          {/* Load More Button */}
          {hasMore && insights.length > 0 && (
            <Button
              variant="outline"
              className="mt-1.5 h-[34px]"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  Loading...
                </>
              ) : (
                "Load more"
              )}
            </Button>
          )}

          {/* End of list indicator */}
          {!hasMore && insights.length > 0 && (
            <div className="py-4 text-center">
              <Text variant="secondary" size="sm">
                You have reached the end of your insights
              </Text>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

export default Insights;
